import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileText, Lock, Plus, Trash2, Unlock } from 'lucide-react';
import { Button, FormField, StatusBadge } from '@/components/ui';
import { Input } from '@/components/ui/input';
import type { StatusKey } from '@/types';
import { getApiErrorMessage } from '@/lib/axios';
import { listTiers } from '@/services/tiers';
import { listEmployes } from '@/services/rh';
import {
  type LocationSortante,
  type ModeGasoil,
  type ModeHeuresSup,
  addAvanceClient,
  addLigneSortante,
  createLocationSortante,
  deleteAvanceClient,
  deleteLigneSortante,
  getLocationSortante,
  listLocationsSortantes,
  rouvrirPointageLocationSortante,
  validerLocationSortante,
  validerPointageLocationSortante,
} from '@/services/locationsSortantes';
import { toast } from '@/store/toastStore';
import { formatDZD } from '@/utils/algeria';
import { CalendrierLocation, type JourPointe } from './CalendrierLocation';

const selectCls = 'flex h-10 w-full rounded-md border border-border bg-surface-secondary px-3 text-sm';
const today = () => new Date().toISOString().slice(0, 10);
const num = (s: string) => Number(s || 0);
const STATUT_UI: Record<string, { label: string; status: StatusKey }> = {
  BROUILLON: { label: 'Brouillon · saisie ouverte', status: 'pending' },
  VALIDE: { label: 'Pointage validé (non facturé)', status: 'working' },
  FACTURE: { label: 'Facturé', status: 'done' },
};
const GASOIL_LABEL: Record<ModeGasoil, string> = {
  DEDUIT: 'Déduit du client (il a fait le plein)',
  CLIENT: 'À la charge du client',
  INCLUS: 'Inclus (mon coût)',
};

export function LocationSortanteTab({ enginId }: { enginId: string }) {
  const qc = useQueryClient();
  const [selId, setSelId] = useState<string | null>(null);
  const { data: locations } = useQuery({ queryKey: ['locations-sortantes', enginId], queryFn: () => listLocationsSortantes(enginId) });
  const { data: clients } = useQuery({ queryKey: ['tiers', 'clients'], queryFn: () => listTiers({ type: 'CLIENT', size: 200 }) });
  const { data: sel } = useQuery({ queryKey: ['location-sortante', selId], queryFn: () => getLocationSortante(selId as string), enabled: !!selId });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['locations-sortantes', enginId] });
    if (selId) qc.invalidateQueries({ queryKey: ['location-sortante', selId] });
  };

  const [form, setForm] = useState<{ client_id: string; tarif_jour: string; mode_heures_sup: ModeHeuresSup; tarif_heure_sup: string; mode_gasoil: ModeGasoil; date_debut: string; date_fin: string }>({
    client_id: '', tarif_jour: '', mode_heures_sup: 'PRORATA', tarif_heure_sup: '', mode_gasoil: 'CLIENT',
    date_debut: today(), date_fin: '',
  });
  const createMut = useMutation({
    mutationFn: () => createLocationSortante({
      engin_id: enginId, client_id: form.client_id, tarif_jour: form.tarif_jour,
      mode_heures_sup: form.mode_heures_sup,
      tarif_heure_sup: form.mode_heures_sup === 'FIXE' ? form.tarif_heure_sup : null,
      mode_gasoil: form.mode_gasoil, date_debut: form.date_debut || undefined, date_fin: form.date_fin || undefined,
    }),
    onSuccess: (l) => { toast.success('Location sortante créée.'); setSelId(l.id); invalidate(); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[340px_1fr]">
      <div className="space-y-3">
        <div className="space-y-2 rounded-lg border border-border bg-surface-secondary p-3">
          <p className="text-xs font-semibold uppercase tracking-wider text-content-secondary">Nouvelle location à un client</p>
          <FormField label="Client (locataire)" required>
            <select className={selectCls} value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })}>
              <option value="">— Sélectionner —</option>
              {clients?.items.map((t) => <option key={t.id} value={t.id}>{t.raison_sociale}</option>)}
            </select>
          </FormField>
          <FormField label="Mon tarif / jour (DA)" required>
            <Input type="number" step="1" min="1" value={form.tarif_jour} onChange={(e) => setForm({ ...form, tarif_jour: e.target.value })} />
          </FormField>
          <FormField label="Heures supplémentaires">
            <select className={selectCls} value={form.mode_heures_sup} onChange={(e) => setForm({ ...form, mode_heures_sup: e.target.value as ModeHeuresSup })}>
              <option value="PRORATA">Prorata (tarif / 8 h)</option>
              <option value="FIXE">Tarif fixe convenu</option>
            </select>
          </FormField>
          {form.mode_heures_sup === 'FIXE' && (
            <FormField label="Tarif heure sup (DA)" required>
              <Input type="number" step="1" min="0" value={form.tarif_heure_sup} onChange={(e) => setForm({ ...form, tarif_heure_sup: e.target.value })} />
            </FormField>
          )}
          <FormField label="Gasoil">
            <select className={selectCls} value={form.mode_gasoil} onChange={(e) => setForm({ ...form, mode_gasoil: e.target.value as ModeGasoil })}>
              {(Object.keys(GASOIL_LABEL) as ModeGasoil[]).map((m) => <option key={m} value={m}>{GASOIL_LABEL[m]}</option>)}
            </select>
          </FormField>
          <div className="grid grid-cols-2 gap-2">
            <FormField label="Du"><Input type="date" value={form.date_debut} onChange={(e) => setForm({ ...form, date_debut: e.target.value })} /></FormField>
            <FormField label="Au"><Input type="date" value={form.date_fin} onChange={(e) => setForm({ ...form, date_fin: e.target.value })} /></FormField>
          </div>
          <Button className="w-full" disabled={!form.client_id || !num(form.tarif_jour)} loading={createMut.isPending} onClick={() => createMut.mutate()}>
            <Plus /> Créer la location
          </Button>
        </div>
        <div className="space-y-1">
          {(locations ?? []).map((l) => (
            <button key={l.id} onClick={() => setSelId(l.id)} className={`flex w-full items-center justify-between rounded-md border px-3 py-2 text-left text-sm ${selId === l.id ? 'border-brand-primary bg-brand-primary/5' : 'border-border hover:bg-surface-hover'}`}>
              <span className="truncate">{l.reference ?? 'Brouillon'} · {formatDZD(num(l.net_a_facturer))}</span>
              <StatusBadge status={STATUT_UI[l.statut]?.status ?? 'pending'} label={STATUT_UI[l.statut]?.label ?? l.statut} />
            </button>
          ))}
          {(locations ?? []).length === 0 && <p className="px-1 py-4 text-center text-xs text-content-muted">Aucune location sortante pour cet engin.</p>}
        </div>
      </div>

      <div>{sel ? <LocationDetail loc={sel} onChange={invalidate} /> : <p className="py-12 text-center text-sm text-content-muted">Sélectionnez ou créez une location.</p>}</div>
    </div>
  );
}

function LocationDetail({ loc, onChange }: { loc: LocationSortante; onChange: () => void }) {
  const readonly = loc.statut !== 'BROUILLON';
  const facture = loc.statut === 'FACTURE';
  const err = (e: unknown) => toast.error(getApiErrorMessage(e));
  // Jours déjà pointés, pour colorer le calendrier.
  const joursPointes: Record<string, JourPointe> = {};
  for (const l of loc.lignes) joursPointes[l.date] = { journees: num(l.journees), heures_sup: num(l.heures_sup) };

  const { data: employes } = useQuery({ queryKey: ['employes', 'mini'], queryFn: () => listEmployes({ actifs_seulement: true }) });
  const [ligne, setLigne] = useState({ date: today(), journees: '1', heures_sup: '0', gasoil_montant: '0', lieu: '', chauffeur_id: '' });
  const [avance, setAvance] = useState({ date_avance: today(), montant: '' });

  const addLigneMut = useMutation({ mutationFn: () => addLigneSortante(loc.id, ligne), onSuccess: () => { setLigne({ date: today(), journees: '1', heures_sup: '0', gasoil_montant: '0', lieu: '', chauffeur_id: '' }); onChange(); }, onError: err });
  const delLigneMut = useMutation({ mutationFn: (id: string) => deleteLigneSortante(id), onSuccess: onChange, onError: err });
  const addAvMut = useMutation({ mutationFn: () => addAvanceClient(loc.id, avance), onSuccess: () => { setAvance({ date_avance: today(), montant: '' }); onChange(); }, onError: err });
  const delAvMut = useMutation({ mutationFn: (id: string) => deleteAvanceClient(id), onSuccess: onChange, onError: err });
  const validerMut = useMutation({ mutationFn: () => validerLocationSortante(loc.id), onSuccess: () => { toast.success('Facture client générée sur le net.'); onChange(); }, onError: err });
  const validerPointageMut = useMutation({ mutationFn: () => validerPointageLocationSortante(loc.id), onSuccess: () => { toast.success('Pointage validé (aucune facture générée).'); onChange(); }, onError: err });
  const rouvrirMut = useMutation({ mutationFn: () => rouvrirPointageLocationSortante(loc.id), onSuccess: () => { toast.success('Pointage rouvert.'); onChange(); }, onError: err });

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-base font-bold text-content-primary">
          {loc.reference ?? 'Location (brouillon)'}
          <span className="ml-2 text-sm font-normal text-content-muted">
            {formatDZD(num(loc.tarif_jour))}/jour · h. sup {formatDZD(num(loc.tarif_heure_applique))} ({loc.mode_heures_sup === 'FIXE' ? 'fixe' : 'prorata'}) · gasoil : {GASOIL_LABEL[loc.mode_gasoil]}
          </span>
        </h3>
        <StatusBadge status={STATUT_UI[loc.statut]?.status ?? 'pending'} label={STATUT_UI[loc.statut]?.label ?? loc.statut} variant="solid" />
      </div>

      {/* Calendrier de pointage : on clique le jour où la machine a travaillé. */}
      <CalendrierLocation
        documentId={loc.id}
        sens="SORTANTE"
        jours={joursPointes}
        moisInitial={loc.date_debut}
        modifiable={!readonly}
        onChange={onChange}
      />

      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-border bg-surface-primary/40 text-left text-xs text-content-muted">
            <th className="px-3 py-2">Date</th><th className="px-2 py-2 text-center">Journées</th><th className="px-2 py-2 text-center">H. sup</th>
            <th className="px-3 py-2 text-right">Gasoil</th><th className="px-3 py-2">Mode</th><th className="px-3 py-2 text-right">Montant</th><th /></tr></thead>
          <tbody>
            {loc.lignes.map((l) => (
              <tr key={l.id} className="border-b border-border/60 last:border-0">
                <td className="px-3 py-1.5">{l.date.slice(8, 10)}/{l.date.slice(5, 7)}/{l.date.slice(0, 4)}</td>
                <td className="px-2 py-1.5 text-center">{num(l.journees)}</td>
                <td className="px-2 py-1.5 text-center">{num(l.heures_sup) || '—'}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{num(l.gasoil_montant) ? formatDZD(num(l.gasoil_montant)) : '—'}</td>
                <td className="px-3 py-1.5 text-xs text-content-muted">{num(l.gasoil_montant) ? l.mode_gasoil_effectif : '—'}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{formatDZD(num(l.montant))}</td>
                <td className="px-2 py-1.5 text-right">{!readonly && <button onClick={() => delLigneMut.mutate(l.id)} className="text-content-muted hover:text-status-stuck" aria-label="Supprimer"><Trash2 className="size-4" /></button>}</td>
              </tr>
            ))}
            {loc.lignes.length === 0 && <tr><td colSpan={7} className="px-3 py-4 text-center text-xs text-content-muted">Aucun jour pointé.</td></tr>}
          </tbody>
        </table>
      </div>
      {!readonly && (
        <div className="grid grid-cols-2 items-end gap-2 sm:grid-cols-7">
          <p className="col-span-2 -mb-1 text-[11px] text-content-muted sm:col-span-7">
            Saisie détaillée (gasoil, lieu…) — pour cocher simplement les jours travaillés, utilisez le calendrier ci-dessus.
          </p>
          <FormField label="Date"><Input type="date" value={ligne.date} onChange={(e) => setLigne({ ...ligne, date: e.target.value })} /></FormField>
          <FormField label="Journées"><select className={selectCls} value={ligne.journees} onChange={(e) => setLigne({ ...ligne, journees: e.target.value })}><option value="1">1 (pleine)</option><option value="0.5">0,5 (demi)</option><option value="0">0</option></select></FormField>
          <FormField label="H. sup"><Input type="number" step="1" min="0" value={ligne.heures_sup} onChange={(e) => setLigne({ ...ligne, heures_sup: e.target.value })} /></FormField>
          <FormField label="Gasoil (DA)"><Input type="number" step="1" min="0" value={ligne.gasoil_montant} onChange={(e) => setLigne({ ...ligne, gasoil_montant: e.target.value })} /></FormField>
          <FormField label="Lieu"><Input value={ligne.lieu} onChange={(e) => setLigne({ ...ligne, lieu: e.target.value })} /></FormField>
          <FormField label="Chauffeur">
            <select className={selectCls} value={ligne.chauffeur_id} onChange={(e) => setLigne({ ...ligne, chauffeur_id: e.target.value })}>
              <option value="">—</option>
              {employes?.items.map((e) => <option key={e.id} value={e.id}>{e.prenom} {e.nom}</option>)}
            </select>
          </FormField>
          <Button loading={addLigneMut.isPending} onClick={() => addLigneMut.mutate()}><Plus /> Ajouter</Button>
        </div>
      )}

      <div>
        <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-content-secondary">Avances reçues du client</p>
        <div className="space-y-1">
          {loc.avances.map((a) => (
            <div key={a.id} className="flex items-center justify-between rounded-md border border-border px-3 py-1.5 text-sm">
              <span>{a.date_avance} · {formatDZD(num(a.montant))}</span>
              {!readonly && <button onClick={() => delAvMut.mutate(a.id)} className="text-content-muted hover:text-status-stuck" aria-label="Supprimer"><Trash2 className="size-4" /></button>}
            </div>
          ))}
          {loc.avances.length === 0 && <p className="text-xs text-content-muted">Aucune avance reçue.</p>}
        </div>
        {!readonly && (
          <div className="mt-2 grid grid-cols-2 items-end gap-2 sm:grid-cols-3">
            <FormField label="Date"><Input type="date" value={avance.date_avance} onChange={(e) => setAvance({ ...avance, date_avance: e.target.value })} /></FormField>
            <FormField label="Montant (DA)"><Input type="number" step="1" min="0" value={avance.montant} onChange={(e) => setAvance({ ...avance, montant: e.target.value })} /></FormField>
            <Button variant="secondary" disabled={!num(avance.montant)} loading={addAvMut.isPending} onClick={() => addAvMut.mutate()}><Plus /> Avance</Button>
          </div>
        )}
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="rounded-lg border border-border bg-surface-secondary p-4 text-sm">
          <Row label="Brut (location)" value={formatDZD(num(loc.total_brut))} />
          <Row label="− Gasoil déduit" value={`− ${formatDZD(num(loc.total_gasoil_deduit))}`} tone="stuck" />
          <Row label="− Avances reçues" value={`− ${formatDZD(num(loc.total_avances))}`} tone="stuck" />
          <div className="mt-2 flex items-center justify-between border-t border-border pt-2 text-base font-bold">
            <span>Net à facturer</span><span className="tabular-nums text-brand-primary">{formatDZD(num(loc.net_a_facturer))}</span>
          </div>
        </div>
        <div className="rounded-lg border border-border bg-surface-secondary p-4 text-sm">
          <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-content-secondary">Marge de l'opération</p>
          <Row label="Revenu net" value={formatDZD(num(loc.net_a_facturer))} />
          <Row label="− Gasoil à ma charge (inclus)" value={`− ${formatDZD(num(loc.total_gasoil_cout))}`} tone="stuck" />
          <div className="mt-2 flex items-center justify-between border-t border-border pt-2 font-bold">
            <span>Marge</span>
            <span className={`tabular-nums ${num(loc.marge) < 0 ? 'text-status-stuck' : 'text-status-done'}`}>{formatDZD(num(loc.marge))}</span>
          </div>
          <p className="mt-1 text-[11px] text-content-muted">Net facturé − gasoil « inclus ». L'entretien n'est pas imputé à l'opération.</p>
          {num(loc.cout_entretien) > 0 && (
            <p className="mt-2 border-t border-border pt-2 text-[11px] text-content-muted">
              Pour information : <strong>{formatDZD(num(loc.cout_entretien))}</strong> d'entretien sur cet engin
              pendant la période (coût de possession, hors marge de cette location).
            </p>
          )}
        </div>
      </div>

      {facture && (
        <p className="rounded-md border border-status-done/40 bg-status-done/10 px-3 py-2 text-sm text-content-primary">
          <FileText className="mr-1 inline size-4" /> Facture client {loc.facture_reference ? <strong>{loc.facture_reference}</strong> : 'brouillon'} générée sur le net — à valider dans Factures (Production).
        </p>
      )}
      {loc.statut === 'VALIDE' && (
        <p className="rounded-md border border-status-working/40 bg-status-working/10 px-3 py-2 text-sm text-content-primary">
          Pointage validé, <strong>sans facture</strong> (location non déclarée). Vous pouvez encore générer la facture ou rouvrir le pointage.
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        {!readonly && (
          <>
            <Button variant="secondary" disabled={num(loc.total_brut) <= 0} loading={validerPointageMut.isPending} onClick={() => validerPointageMut.mutate()}>
              <Lock /> Valider le pointage (sans facture)
            </Button>
            <Button className="flex-1" disabled={num(loc.total_brut) <= 0} loading={validerMut.isPending} onClick={() => validerMut.mutate()}>
              <FileText /> Générer la facture client (net {formatDZD(num(loc.net_a_facturer))})
            </Button>
          </>
        )}
        {loc.statut === 'VALIDE' && (
          <Button className="flex-1" disabled={num(loc.total_brut) <= 0} loading={validerMut.isPending} onClick={() => validerMut.mutate()}>
            <FileText /> Générer la facture client (net {formatDZD(num(loc.net_a_facturer))})
          </Button>
        )}
        {readonly && (
          <Button variant="secondary" loading={rouvrirMut.isPending}
            onClick={() => { if (window.confirm(facture ? 'Rouvrir le pointage ? La facture brouillon liée sera supprimée.' : 'Rouvrir le pointage pour correction ?')) rouvrirMut.mutate(); }}>
            <Unlock /> Rouvrir le pointage
          </Button>
        )}
      </div>
    </div>
  );
}

function Row({ label, value, tone }: { label: string; value: string; tone?: 'stuck' }) {
  return (
    <div className="flex items-center justify-between py-0.5">
      <span className="text-content-secondary">{label}</span>
      <span className={`tabular-nums ${tone === 'stuck' ? 'text-status-stuck' : 'text-content-primary'}`}>{value}</span>
    </div>
  );
}
